import React, { ErrorInfo } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { i18n } from 'translations/i18n';
import { Link, Typography } from '@mui/material';
import { LangNamespaceEnum } from 'shared/enums/LangNamespaceEnum';
import { LangCodeEnum } from 'shared/enums/LangCodeEnum';

type ErrorBoundaryProps = React.PropsWithChildren;

type ErrorBoundaryPageState = { hasError: boolean };

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryPageState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // React error boundaries swallow errors before window.onerror, so RUM needs
    // an explicit addError; console.error also flows to Datadog Logs via
    // forwardConsoleLogs.
    console.error(error, info);
    datadogRum.addError(error, { componentStack: info.componentStack });

    this.setState({ hasError: true });
  }

  render() {
    if (this.state.hasError) {
      let lng = LangCodeEnum.ENGLISH_BRITISH;
      const ns = LangNamespaceEnum.SURVEY_UI_COMPONENTS;
      const preferredLanguage = sessionStorage.getItem('preferredLanguage');

      if (preferredLanguage) {
        try {
          lng = JSON.parse(preferredLanguage);
        } catch (error) {
          console.warn('Failed to parse preferred language from sessionStorage:', error);
        }
      }

      return (
        <>
          <Typography component="h1" variant="h5">
            {i18n.t('ErrorBoundary.title', { lng, ns })}
          </Typography>
          <Typography variant="h5" mb={3}>
            {i18n.t('ErrorBoundary.description', { lng, ns })}
            <Link href="mailto:support@workbuzz.com" variant="h5" target="_blank">
              support@workbuzz.com
            </Link>
          </Typography>
        </>
      );
    }

    return this.props.children;
  }
}
